'use client';

import React, { type ReactNode, useCallback, useState, useSyncExternalStore } from 'react';
import { cva, cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';
import { Icon, type IconName } from '~/components/primitives';
import { useMountEffect } from '~/utils/react/client';

export type AccordionItem = {
    uid: string;
    title: string;
    description: string;
    icon?: IconName;
    bridgeAttributes?: object;
};

type Color = 'light' | 'dark';

function slugify(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

/** Presentational: one open index, exclusive accordion behavior. */
function AccordionList({
    color,
    items,
    activeIndex,
    highlightQuery,
    onItemToggle,
}: {
    color: 'light' | 'dark';
    items: AccordionItem[];
    activeIndex: number;
    highlightQuery?: string;
    onItemToggle: (index: number) => void;
}): ReactNode {
    return (
        <div className="group flex flex-col">
            {items.map((item, index) => (
                <AccordionItemComponent
                    {...item.bridgeAttributes}
                    color={color}
                    key={item.uid}
                    slug={slugify(item.title)}
                    title={item.title}
                    icon={item.icon}
                    isOpen={activeIndex === index}
                    isFirst={index === 0}
                    prevIsOpen={index > 0 && activeIndex === index - 1}
                    highlightQuery={highlightQuery}
                    onToggle={() => onItemToggle(index)}
                >
                    {item.description}
                </AccordionItemComponent>
            ))}
        </div>
    );
}

/**
 * FAQ search results: local state only — no URL hash, no effects.
 * Remount via `key={query}` in the parent when the query changes so each search gets a fresh open row.
 */
export function SearchAccordion({
    color,
    items,
    highlightQuery,
    forceOpenUid,
}: {
    color: Color;
    items: AccordionItem[];
    highlightQuery?: string;
    forceOpenUid?: string | null;
}): ReactNode {
    const [openIndex, setOpenIndex] = useState(() => {
        const forcedIndex = forceOpenUid ? items.findIndex(item => item.uid === forceOpenUid) : -1;
        return forcedIndex >= 0 ? forcedIndex : 0;
    });

    function handleItemToggle(index: number) {
        setOpenIndex(cur => (cur === index ? -1 : index));
    }

    return (
        <AccordionList
            color={color}
            items={items}
            activeIndex={openIndex}
            highlightQuery={highlightQuery}
            onItemToggle={handleItemToggle}
        />
    );
}

/**
 * In-page FAQ: syncs open row with `#slug` for deep links and replaceState on toggle.
 */
const Accordion: React.FC<{
    color: Color;
    items: AccordionItem[];
    /** Search query to highlight in titles and descriptions */
    highlightQuery?: string;
}> = props => {
    const hash = useSyncExternalStore(subscribeToHash, readHash, () => '');
    // The last toggle, with the hash it left behind. A hash that differs from it was navigated to, and opens its item.
    const [toggle, setToggle] = useState<{ index: number; hash: string }>();
    const hashIndex = props.items.findIndex(item => slugify(item.title) === hash);
    const openItemIndex =
        toggle?.hash !== hash && hashIndex >= 0 ? hashIndex : (toggle?.index ?? 0);

    useMountEffect(() => {
        requestAnimationFrame(scrollToHash);
    });

    function handleItemToggle(index: number) {
        const newIndex = openItemIndex === index ? -1 : index;
        const slug = slugify(props.items[index]!.title);
        setToggle({ index: newIndex, hash: newIndex >= 0 ? slug : '' });
        if (newIndex >= 0) {
            window.history.replaceState(null, '', `#${slug}`);
        } else {
            window.history.replaceState(
                null,
                '',
                window.location.pathname + window.location.search,
            );
        }
    }

    return (
        <AccordionList
            color={props.color}
            items={props.items}
            activeIndex={openItemIndex}
            highlightQuery={props.highlightQuery}
            onItemToggle={handleItemToggle}
        />
    );
};

export default Accordion;

function readHash() {
    return window.location.hash.slice(1);
}

function scrollToHash() {
    const hash = readHash();
    if (hash)
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function subscribeToHash(onChange: () => void) {
    const onHashChange = () => {
        scrollToHash();
        onChange();
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
}

const IconVariant = cva('', {
    variants: {
        variant: {
            light: 'text-white',
            dark: 'text-tertiary',
        },
    },
});

const textVariant = cva('', {
    variants: {
        variant: {
            light: 'text-white',
            dark: 'text-blue',
        },
    },
});

const borderVariant = cva('', {
    variants: {
        variant: {
            light: 'border-white/20',
            dark: 'border-brown-200',
        },
    },
});

function AccordionItemComponent({
    title,
    slug,
    icon = 'check-circle',
    color,
    children,
    isOpen,
    isFirst,
    prevIsOpen,
    highlightQuery,
    onToggle,
    ...props
}: {
    title: string;
    slug: string;
    icon?: IconName;
    color: Color;
    children: React.ReactNode;
    isOpen: boolean;
    isFirst: boolean;
    prevIsOpen: boolean;
    highlightQuery?: string;
    onToggle: () => void;
}): ReactNode {
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onToggle();
        },
        [onToggle],
    );

    const showBorder = !isFirst && !isOpen && !prevIsOpen;

    return (
        <button
            {...props}
            type="button"
            data-testid="accordion-trigger"
            id={slug}
            aria-expanded={isOpen}
            onClick={handleClick}
            className="relative block w-full cursor-pointer scroll-mt-32 text-left"
        >
            <div
                className={cx(
                    'mx-5 border-t transition-opacity duration-200 lg:mx-12',
                    borderVariant({ variant: color }),
                    showBorder ? 'opacity-100' : 'opacity-0',
                )}
            />

            <div
                className={cx(
                    'rounded-2xl px-5 py-5 transition-colors duration-200 ease-out lg:px-10 lg:py-6',
                    isOpen ? 'bg-white' : 'bg-transparent hover:bg-white/[0.08]',
                )}
            >
                <div
                    className={cx(
                        'flex w-full items-start justify-between text-left md:items-center',
                        isOpen ? 'text-blue' : textVariant({ variant: color }),
                    )}
                >
                    <Icon
                        className={cx(
                            'mr-4 hidden shrink-0 lg:block',
                            isOpen ? 'text-tertiary' : IconVariant({ variant: color }),
                        )}
                        name={icon}
                    />
                    <span className="text-lg leading-6 font-bold">
                        {highlightQuery ? highlightText(title, highlightQuery) : title}
                    </span>
                    <div className="ml-auto pl-4">
                        <motion.div
                            animate={{ rotate: isOpen ? 45 : 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                        >
                            <Icon
                                className={cx(
                                    'fill-current',
                                    isOpen ? 'text-tertiary' : IconVariant({ variant: color }),
                                )}
                                name="plus"
                            />
                        </motion.div>
                    </div>
                </div>

                <AnimatePresence initial={false}>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className="overflow-hidden text-blue-700 lg:ml-10"
                        >
                            <div className="pt-3">
                                {highlightQuery && typeof children === 'string'
                                    ? highlightText(children, highlightQuery)
                                    : children}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </button>
    );
}

function highlightText(text: string, query: string): React.ReactNode {
    if (!query.trim()) return text;

    const words = query
        .trim()
        .split(/\s+/)
        .filter(w => w.length >= 2)
        .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    if (words.length === 0) return text;

    const parts = text.split(new RegExp(`(${words.join('|')})`, 'i'));

    if (parts.length === 1) return text;

    return parts.map((part, i) =>
        i % 2 === 1 ? (
            <mark key={i} className="rounded-sm bg-turqoise-500/30 text-inherit">
                {part}
            </mark>
        ) : (
            part
        ),
    );
}
